import {
  DAY_END_MIN,
  DAY_START_MIN,
  DEFAULT_EST_MIN,
  PEAK_END_MIN,
  PEAK_START_MIN,
  SLOT_MIN,
} from "@founderdeck/core";
import { useRef, useState } from "react";
import type { Project, Task } from "../lib/api";
import { hourLabel, nowMinutes, pad, parseKey } from "../lib/date";
import { priColor } from "../lib/tasks";

const PXH = 56; // pixels per hour
const yOf = (min: number) => ((min - DAY_START_MIN) / 60) * PXH;

interface Layout {
  task: Task;
  top: number;
  height: number;
  col: number;
  cols: number;
}

/** Side-by-side column layout for overlapping timeboxes (calendar-style). */
function layout(tasks: Task[], estOf: (t: Task) => number): Layout[] {
  const evs = tasks
    .slice()
    .sort((a, b) => a.fromHour! - b.fromHour!)
    .map((t) => ({
      t,
      s: t.fromHour!,
      e: t.fromHour! + Math.max(26, estOf(t)),
      col: 0,
      cols: 1,
    }));

  let cluster: typeof evs = [];
  let clusterMaxE = -Infinity;
  const flush = () => {
    const colEnds: number[] = [];
    for (const ev of cluster) {
      let ci = 0;
      while (colEnds[ci] != null && colEnds[ci]! > ev.s + 1e-6) ci++;
      ev.col = ci;
      colEnds[ci] = ev.e;
    }
    for (const ev of cluster) ev.cols = colEnds.length;
  };

  for (const ev of evs) {
    if (cluster.length && ev.s >= clusterMaxE - 1e-6) {
      flush();
      cluster = [];
      clusterMaxE = -Infinity;
    }
    cluster.push(ev);
    clusterMaxE = Math.max(clusterMaxE, ev.e);
  }
  flush();

  return evs.map((ev) => ({
    task: ev.t,
    top: yOf(ev.t.fromHour!),
    height: Math.max(24, (estOf(ev.t) / 60) * PXH),
    col: ev.col,
    cols: ev.cols,
  }));
}

export function DayGrid({
  tasks,
  projects,
  date,
  isToday,
  onSchedule,
  onUnschedule,
  onToggle,
  onResize,
}: {
  tasks: Task[];
  projects: Map<string, Project>;
  date: string;
  isToday: boolean;
  onSchedule: (id: string, rawFromMin: number) => void;
  onUnschedule: (id: string) => void;
  onToggle: (t: Task) => void;
  onResize: (id: string, estMin: number) => void;
}) {
  const laneRef = useRef<HTMLDivElement>(null);
  const resizingRef = useRef(false);
  const startRef = useRef<{ y: number; base: number } | null>(null);
  const [resize, setResize] = useState<{ id: string; est: number } | null>(null);
  const [dropOk, setDropOk] = useState(false);

  const estOf = (t: Task) =>
    resize && resize.id === t.id ? resize.est : t.estMin ?? DEFAULT_EST_MIN;

  const scheduled = tasks.filter((t) => t.fromHour != null);
  const laid = layout(scheduled, estOf);
  const now = nowMinutes();
  const hours: number[] = [];
  for (let h = DAY_START_MIN / 60; h < DAY_END_MIN / 60; h++) hours.push(h);
  const laneHeight = ((DAY_END_MIN - DAY_START_MIN) / 60) * PXH;

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDropOk(false);
    const id = e.dataTransfer.getData("text/plain");
    if (!id || !laneRef.current) return;
    const rect = laneRef.current.getBoundingClientRect();
    const rawMin = DAY_START_MIN + ((e.clientY - rect.top) / PXH) * 60;
    onSchedule(id, rawMin);
  }

  function beginResize(e: React.PointerEvent, t: Task) {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = true;
    startRef.current = { y: e.clientY, base: t.estMin ?? DEFAULT_EST_MIN };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setResize({ id: t.id, est: t.estMin ?? DEFAULT_EST_MIN });
  }
  function moveResize(e: React.PointerEvent, t: Task) {
    if (!resizingRef.current || !startRef.current) return;
    const dMin = ((e.clientY - startRef.current.y) / PXH) * 60;
    let est = Math.round((startRef.current.base + dMin) / SLOT_MIN) * SLOT_MIN;
    est = Math.max(SLOT_MIN, est);
    est = Math.min(est, DAY_END_MIN - (t.fromHour ?? DAY_START_MIN)); // stay in day
    setResize({ id: t.id, est });
  }
  function endResize(t: Task) {
    if (!resizingRef.current) return;
    resizingRef.current = false;
    const est = resize?.est ?? t.estMin ?? DEFAULT_EST_MIN;
    setResize(null);
    if (est !== (t.estMin ?? DEFAULT_EST_MIN)) onResize(t.id, est);
  }

  return (
    <div className="gridwrap">
      <div className="gridhead">
        <span className="gt">
          {parseKey(date).toLocaleDateString("en-GB", {
            weekday: "long",
            day: "numeric",
            month: "short",
          })}
        </span>
        <span className="caplab">drag tasks in · drag edge to resize</span>
      </div>
      <div className="gridbody">
        <div className="gcol" style={{ height: laneHeight }}>
          {hours.map((h) => (
            <div key={h} className="glab">
              {pad(h)}
            </div>
          ))}
        </div>
        <div
          ref={laneRef}
          className={`glane${dropOk ? " dropok" : ""}`}
          style={{ height: laneHeight }}
          onDragOver={(e) => {
            e.preventDefault();
            if (!dropOk) setDropOk(true);
          }}
          onDragLeave={() => setDropOk(false)}
          onDrop={onDrop}
        >
          {Array.from({ length: (DAY_END_MIN - DAY_START_MIN) / SLOT_MIN + 1 }).map(
            (_, i) => (
              <div
                key={i}
                className={`gline${i % 2 ? " half" : ""}`}
                style={{ top: (i * PXH) / 2 }}
              />
            ),
          )}
          <div
            className="peak"
            style={{ top: yOf(PEAK_START_MIN), height: yOf(PEAK_END_MIN) - yOf(PEAK_START_MIN) }}
          >
            <span>PEAK — deep work</span>
          </div>

          {laid.map(({ task, top, height, col, cols }) => {
            const p = task.projectId ? projects.get(task.projectId) : undefined;
            const color = p?.color ?? priColor(task.pri);
            const est = estOf(task);
            const side =
              cols > 1
                ? {
                    left: `calc(${(col / cols) * 100}% + 8px)`,
                    width: `calc(${100 / cols}% - 12px)`,
                    right: "auto" as const,
                  }
                : {};
            return (
              <div
                key={task.id}
                className={`gtask${task.status === "done" ? " done" : ""}`}
                style={{
                  top,
                  height,
                  background: `${color}26`,
                  borderColor: `${color}66`,
                  ...side,
                }}
                draggable
                onDragStart={(e) => {
                  if (resizingRef.current) return e.preventDefault();
                  e.dataTransfer.setData("text/plain", task.id);
                }}
              >
                <b style={{ color }}>{task.title}</b>
                <i>
                  {hourLabel(task.fromHour!)}–{hourLabel(task.fromHour! + est)} · {est}m
                  {task.energy === "high" ? " ⚡" : ""}
                </i>
                <button
                  className="gunsched"
                  title="Remove from grid"
                  onClick={() => onUnschedule(task.id)}
                >
                  ↩
                </button>
                <button
                  className={`chk gchk${task.status === "done" ? " on" : ""}`}
                  onClick={() => onToggle(task)}
                  aria-label="Toggle done"
                />
                <div
                  className="rsz"
                  onPointerDown={(e) => beginResize(e, task)}
                  onPointerMove={(e) => moveResize(e, task)}
                  onPointerUp={() => endResize(task)}
                />
              </div>
            );
          })}

          {isToday && now >= DAY_START_MIN && now <= DAY_END_MIN && (
            <div className="gnow" style={{ top: yOf(now) }} />
          )}
        </div>
      </div>
    </div>
  );
}
